import { useState, useRef, useEffect, useCallback } from 'react';
import { APP } from './tokens';
import type { Folder } from '../../types';
import { AppFolderIcon } from './AppFolderIcon';

// ─── Types ──────────────────────────────────────

export interface FolderWithCount extends Folder {
  conversationCount: number;
}

export interface ProjectFolderRecentSource {
  fileId: string;
  fileName: string;
  mimeType: string;
  title: string;
  updatedAt: Date | string | number;
}

export interface CreateProjectFolderInput {
  name: string;
  inviteEmails: string[];
  recentSource?: ProjectFolderRecentSource | null;
  localFile?: File | null;
  recentSources?: ProjectFolderRecentSource[];
  localFiles?: File[];
}

interface FoldersPanelProps {
  folders: FolderWithCount[];
  onCreateFolder: (name: string) => void;
  onCreateProjectFolder?: (input: CreateProjectFolderInput) => Promise<void> | void;
  recentProjectSources?: ProjectFolderRecentSource[];
  onRenameFolder: (id: string, name: string) => void;
  onDeleteFolder: (id: string) => void;
  onDownloadFolder?: (id: string) => void;
  onOpenFolder: (id: string) => void;
  /** Called when the back button is clicked to return to the previous screen */
  onBack?: () => void;
}

// ─── Icons ──────────────────────────────────────

function ChevronRight() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
      <path d="M5 3l5 4.5L5 12" stroke="#8B7D6B" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
      <path d="M2 4h10M5 4V2.5h4V4M5.5 7v3.5M8.5 7v3.5M3 4l.7 7.5h6.6L11 4" stroke="#8B7D6B" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── Inline rename input ────────────────────────

function RenameInput({
  initialValue,
  onCommit,
  onCancel,
}: {
  initialValue: string;
  onCommit: (name: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initialValue);
  const ref = useRef<HTMLInputElement>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);

  const commit = useCallback(() => {
    if (cancelledRef.current) return;
    const trimmed = value.trim();
    if (trimmed && trimmed !== initialValue) {
      onCommit(trimmed);
    } else {
      onCancel();
    }
  }, [value, initialValue, onCommit, onCancel]);

  return (
    <input
      ref={ref}
      type="text"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') {
          cancelledRef.current = true;
          onCancel();
        }
      }}
      style={{
        flexGrow: 1,
        borderRadius: 8,
        paddingBlock: 6,
        paddingInline: 10,
        backgroundColor: APP.bgCardBody,
        border: '1px solid #8B7355',
        boxShadow: '#8B73551A 0px 0px 0px 3px',
        fontFamily: APP.fontFamily,
        fontSize: 13,
        fontWeight: 400,
        color: APP.textPrimary,
        lineHeight: '16px',
        outline: 'none',
      }}
    />
  );
}

// ─── New project row ─────────────────────────────

function NewFolderRow({ onCreate }: { onCreate: () => void }) {
  return (
    <button
      type="button"
      onClick={onCreate}
      style={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        borderRadius: 12,
        paddingBlock: 14,
        paddingInline: 16,
        gap: 8,
        backgroundColor: '#FEFCF9',
        border: '1px solid #E0DCD4',
        boxShadow: '#1A17140A 0px 1px 2px',
        cursor: 'pointer',
        fontFamily: APP.fontFamily,
        fontSize: 13,
        fontWeight: 400,
        color: '#8B7D6B',
        lineHeight: '16px',
        textAlign: 'left',
      }}
    >
      <AppFolderIcon plus />
      Create project
    </button>
  );
}

function normalizeInviteEmail(value: string) {
  return value.trim().toLowerCase();
}

function parseInviteEmails(value: string) {
  return value
    .split(/[\s,;]+/)
    .map(normalizeInviteEmail)
    .filter(Boolean);
}

function isValidInviteEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function SelectedDocumentRow({
  label,
  sublabel,
  onRemove,
}: {
  label: string;
  sublabel?: string;
  onRemove: () => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, borderRadius: 8, border: `1px solid ${APP.border}`, backgroundColor: APP.bgInputBar, padding: '8px 9px' }}>
      <span style={{ width: 18, height: 18, borderRadius: 5, backgroundColor: '#F5E7DC', color: '#6D3518', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, flexShrink: 0 }}>
        F
      </span>
      <span style={{ minWidth: 0, flex: 1 }}>
        <span style={{ display: 'block', color: APP.textPrimary, fontSize: 12, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </span>
        {sublabel && (
          <span style={{ display: 'block', marginTop: 1, color: APP.textMuted, fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {sublabel}
          </span>
        )}
      </span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${label}`}
        style={{ border: 'none', background: 'transparent', color: APP.textMuted, cursor: 'pointer', padding: 2, fontSize: 16, lineHeight: '16px' }}
      >
        ×
      </button>
    </div>
  );
}

function ProjectFolderCreateModal({
  recentSources,
  onClose,
  onCreate,
}: {
  recentSources: ProjectFolderRecentSource[];
  onClose: () => void;
  onCreate: (input: CreateProjectFolderInput) => Promise<void> | void;
}) {
  const [name, setName] = useState('');
  const [inviteDraft, setInviteDraft] = useState('');
  const [inviteEmails, setInviteEmails] = useState<string[]>([]);
  const [tab, setTab] = useState<'recent' | 'computer'>('recent');
  const [selectedRecentSources, setSelectedRecentSources] = useState<ProjectFolderRecentSource[]>([]);
  const [localFiles, setLocalFiles] = useState<File[]>([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedDocumentCount = selectedRecentSources.length + localFiles.length;
  const selectedSeedLabel =
    selectedDocumentCount === 0
      ? 'No documents selected'
      : selectedDocumentCount === 1
        ? (selectedRecentSources[0]?.fileName ?? localFiles[0]?.name ?? '1 document')
        : `${selectedDocumentCount} documents`;
  const memberCount = 1 + inviteEmails.length;

  const toggleRecentSource = useCallback((source: ProjectFolderRecentSource) => {
    setSelectedRecentSources((current) => (
      current.some((item) => item.fileId === source.fileId)
        ? current.filter((item) => item.fileId !== source.fileId)
        : [...current, source]
    ));
  }, []);

  const addLocalFiles = useCallback((files: FileList | null) => {
    if (!files?.length) return;
    const nextFiles = Array.from(files).filter((file) => file.size > 0);
    setLocalFiles((current) => {
      const seen = new Set(current.map((file) => `${file.name}:${file.size}:${file.lastModified}`));
      return [
        ...current,
        ...nextFiles.filter((file) => {
          const key = `${file.name}:${file.size}:${file.lastModified}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        }),
      ];
    });
  }, []);

  const addInviteDraft = useCallback((rawValue = inviteDraft) => {
    const nextEmails = parseInviteEmails(rawValue);
    if (nextEmails.length === 0) return true;

    const invalid = nextEmails.find((email) => !isValidInviteEmail(email));
    if (invalid) {
      setError(`"${invalid}" is not a valid email address.`);
      return false;
    }

    setInviteEmails((current) => Array.from(new Set([...current, ...nextEmails])));
    setInviteDraft('');
    setError(null);
    return true;
  }, [inviteDraft]);

  const removeInvite = useCallback((email: string) => {
    setInviteEmails((current) => current.filter((item) => item !== email));
  }, []);

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Give this project a name.');
      return;
    }
    const draftEmails = parseInviteEmails(inviteDraft);
    const invalid = draftEmails.find((email) => !isValidInviteEmail(email));
    if (invalid) {
      setError(`"${invalid}" is not a valid email address.`);
      return;
    }
    const finalInviteEmails = Array.from(new Set([...inviteEmails, ...draftEmails]));
    setCreating(true);
    setError(null);
    try {
      await onCreate({
        name: trimmed,
        inviteEmails: finalInviteEmails,
        recentSource: selectedRecentSources[0] ?? null,
        localFile: localFiles[0] ?? null,
        recentSources: selectedRecentSources,
        localFiles,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create this project.');
      setCreating(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Create project"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 80,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        backgroundColor: 'rgba(26, 23, 20, 0.36)',
      }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        style={{
          width: 'min(640px, 100%)',
          maxHeight: 'min(760px, calc(100vh - 40px))',
          overflow: 'auto',
          borderRadius: 12,
          backgroundColor: APP.bgCardBody,
          border: `1px solid ${APP.border}`,
          boxShadow: '0 24px 60px rgba(26, 23, 20, 0.22)',
          fontFamily: APP.fontFamily,
        }}
      >
        <div style={{ padding: 20, borderBottom: `1px solid ${APP.border}` }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <AppFolderIcon plus width={28} height={22} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2 style={{ margin: 0, fontSize: 20, lineHeight: '26px', color: APP.textPrimary, fontWeight: 600 }}>
                Create project
              </h2>
              <p style={{ margin: '5px 0 0', fontSize: 13, lineHeight: '18px', color: APP.textMuted }}>
                Name the project, invite collaborators, and start from history, your computer, or an empty Corner workspace.
              </p>
            </div>
            <button type="button" onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: APP.textMuted, fontSize: 20, lineHeight: '20px' }}>
              ×
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 18, padding: 20 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 7, fontSize: 12, fontWeight: 600, color: APP.textPrimary }}>
            Project name
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="e.g. Carter acquisition"
              style={{
                borderRadius: 8,
                border: `1px solid ${APP.border}`,
                backgroundColor: APP.bgInputBar,
                color: APP.textPrimary,
                padding: '11px 12px',
                fontSize: 14,
                fontFamily: APP.fontFamily,
                outline: 'none',
              }}
            />
          </label>

          <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: APP.textPrimary }}>Members</div>
              <div style={{ fontSize: 11, color: APP.textMuted }}>
                {memberCount} {memberCount === 1 ? 'member' : 'members'} · {inviteEmails.length} invited
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
              {[
                ['Members', String(memberCount)],
                ['Collaborating projects', '1'],
                ['Starting documents', selectedSeedLabel],
              ].map(([label, value]) => (
                <div key={label} style={{ border: `1px solid ${APP.border}`, borderRadius: 10, padding: 10, backgroundColor: '#FEFCF9', minWidth: 0 }}>
                  <div style={{ fontSize: 10, lineHeight: '13px', color: APP.textMuted, fontWeight: 600, textTransform: 'uppercase' }}>
                    {label}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 13, lineHeight: '17px', color: APP.textPrimary, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {value}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ border: `1px solid ${APP.border}`, borderRadius: 10, padding: 10, backgroundColor: APP.bgCardBody }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, borderRadius: 999, padding: '6px 9px', backgroundColor: '#F5E7DC', color: '#6D3518', fontSize: 12, fontWeight: 700 }}>
                  You
                  <span style={{ color: '#9A6A4B', fontWeight: 600 }}>owner</span>
                </span>
                {inviteEmails.map((email) => (
                  <span key={email} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, borderRadius: 999, padding: '6px 8px 6px 10px', backgroundColor: APP.bgInputBar, border: `1px solid ${APP.border}`, color: APP.textPrimary, fontSize: 12, fontWeight: 600 }}>
                    {email}
                    <button
                      type="button"
                      onClick={() => removeInvite(email)}
                      aria-label={`Remove ${email}`}
                      style={{ border: 'none', background: 'transparent', color: APP.textMuted, cursor: 'pointer', padding: 0, fontSize: 15, lineHeight: '14px' }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <input
                  value={inviteDraft}
                  onChange={(event) => setInviteDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ',') {
                      event.preventDefault();
                      addInviteDraft();
                    }
                  }}
                  placeholder="Add teammate or client email"
                  style={{
                    flex: 1,
                    minWidth: 0,
                    borderRadius: 8,
                    border: `1px solid ${APP.border}`,
                    backgroundColor: APP.bgInputBar,
                    color: APP.textPrimary,
                    padding: '10px 11px',
                    fontSize: 13,
                    fontFamily: APP.fontFamily,
                    outline: 'none',
                  }}
                />
                <button
                  type="button"
                  onClick={() => addInviteDraft()}
                  disabled={!inviteDraft.trim()}
                  style={{
                    borderRadius: 8,
                    border: `1px solid ${APP.border}`,
                    backgroundColor: inviteDraft.trim() ? APP.textPrimary : APP.bgInputBar,
                    color: inviteDraft.trim() ? '#fff' : APP.textMuted,
                    padding: '0 12px',
                    cursor: inviteDraft.trim() ? 'pointer' : 'not-allowed',
                    fontSize: 12,
                    fontWeight: 700,
                    fontFamily: APP.fontFamily,
                  }}
                >
                  Add
                </button>
              </div>
              <div style={{ marginTop: 7, fontSize: 11, lineHeight: '15px', color: APP.textMuted }}>
                Press Enter, comma, or Add. Invites are sent after the project is created.
              </div>
            </div>
          </section>

          <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: APP.textPrimary }}>Upload section</div>
              <div style={{ marginTop: 3, fontSize: 12, color: APP.textMuted }}>You do not have to start from scratch.</div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {(['recent', 'computer'] as const).map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTab(key)}
                  style={{
                    borderRadius: 999,
                    border: `1px solid ${tab === key ? '#9A5A2F' : APP.border}`,
                    backgroundColor: tab === key ? '#F5E7DC' : APP.bgCardBody,
                    color: tab === key ? '#6D3518' : APP.textMuted,
                    padding: '7px 12px',
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: APP.fontFamily,
                  }}
                >
                  {key === 'recent' ? 'From history' : 'From computer'}
                </button>
              ))}
            </div>

            {tab === 'recent' ? (
              <div style={{ border: `1px solid ${APP.border}`, borderRadius: 10, overflow: 'hidden', backgroundColor: APP.bgCardBody }}>
                {recentSources.length === 0 ? (
                  <div style={{ padding: 16, fontSize: 13, color: APP.textMuted, lineHeight: '18px' }}>
                    No recent documents yet. You can still create an empty project or upload from your computer.
                  </div>
                ) : recentSources.map((source) => {
                  const selected = selectedRecentSources.some((item) => item.fileId === source.fileId);
                  return (
                    <button
                      key={source.fileId}
                      type="button"
                      onClick={() => toggleRecentSource(source)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        gap: 10,
                        padding: '11px 13px',
                        border: 'none',
                        borderBottom: `1px solid ${APP.border}`,
                        backgroundColor: selected ? '#F7EFE8' : 'transparent',
                        cursor: 'pointer',
                        textAlign: 'left',
                        fontFamily: APP.fontFamily,
                      }}
                    >
                      <span style={{ width: 16, height: 16, borderRadius: 5, border: `1px solid ${selected ? '#9A5A2F' : APP.border}`, backgroundColor: selected ? '#9A5A2F' : 'transparent', color: '#fff', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, lineHeight: '14px' }}>
                        {selected ? '✓' : ''}
                      </span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: APP.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {source.fileName}
                        </span>
                        <span style={{ display: 'block', marginTop: 2, fontSize: 11, color: APP.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {source.title}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <label
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 132,
                  gap: 8,
                  border: `1px dashed ${APP.border}`,
                  borderRadius: 10,
                  backgroundColor: APP.bgInputBar,
                  cursor: 'pointer',
                  color: APP.textMuted,
                  textAlign: 'center',
                  padding: 16,
                }}
              >
                <input
                  type="file"
                  style={{ display: 'none' }}
                  multiple
                  onChange={(event) => {
                    addLocalFiles(event.target.files);
                    event.currentTarget.value = '';
                  }}
                />
                <span style={{ fontSize: 13, color: APP.textPrimary, fontWeight: 600 }}>
                  Choose documents from your computer
                </span>
                <span style={{ fontSize: 12, lineHeight: '17px' }}>
                  PDF, DOCX, images, and common document formats are supported.
                </span>
              </label>
            )}
            {(selectedRecentSources.length > 0 || localFiles.length > 0) && (
              <div style={{ border: `1px solid ${APP.border}`, borderRadius: 10, backgroundColor: APP.bgCardBody, padding: 10, display: 'flex', flexDirection: 'column', gap: 7 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: APP.textMuted, textTransform: 'uppercase' }}>
                  Selected documents
                </div>
                {selectedRecentSources.map((source) => (
                  <SelectedDocumentRow
                    key={`recent:${source.fileId}`}
                    label={source.fileName}
                    sublabel={source.title}
                    onRemove={() => setSelectedRecentSources((current) => current.filter((item) => item.fileId !== source.fileId))}
                  />
                ))}
                {localFiles.map((file) => (
                  <SelectedDocumentRow
                    key={`local:${file.name}:${file.size}:${file.lastModified}`}
                    label={file.name}
                    sublabel={`${Math.max(1, Math.round(file.size / 1024))} KB`}
                    onRemove={() => setLocalFiles((current) => current.filter((item) => item !== file))}
                  />
                ))}
              </div>
            )}
          </section>

          {error && (
            <div role="alert" style={{ borderRadius: 8, padding: '10px 12px', backgroundColor: '#FFF4F2', border: '1px solid #E8B7AE', color: '#9D2E22', fontSize: 12, lineHeight: '17px' }}>
              {error}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: 20, borderTop: `1px solid ${APP.border}` }}>
          <button type="button" onClick={onClose} disabled={creating} style={{ borderRadius: 8, border: `1px solid ${APP.border}`, backgroundColor: 'transparent', color: APP.textMuted, padding: '10px 14px', cursor: creating ? 'not-allowed' : 'pointer', fontFamily: APP.fontFamily, fontSize: 13, fontWeight: 600 }}>
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={creating} style={{ borderRadius: 8, border: 'none', backgroundColor: APP.textPrimary, color: '#fff', padding: '10px 16px', cursor: creating ? 'not-allowed' : 'pointer', opacity: creating ? 0.65 : 1, fontFamily: APP.fontFamily, fontSize: 13, fontWeight: 700 }}>
            {creating ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Project row ─────────────────────────────────

function FolderRow({
  folder,
  onOpen,
  onRename,
  onDelete,
  onDownload,
}: {
  folder: FolderWithCount;
  onOpen: () => void;
  onRename: (name: string) => void;
  onDelete: () => void;
  onDownload?: () => void;
}) {
  const [renaming, setRenaming] = useState(false);
  const countLabel =
    folder.conversationCount === 1
      ? '1 item'
      : `${folder.conversationCount} items`;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        minHeight: 52,
        paddingBlock: 12,
        paddingInline: 16,
        gap: 12,
        borderBottom: '1px solid #F2EDE6',
        cursor: renaming ? undefined : 'pointer',
      }}
      onClick={renaming ? undefined : onOpen}
    >
      <AppFolderIcon />
      {renaming ? (
        <RenameInput
          initialValue={folder.name}
          onCommit={(name) => {
            onRename(name);
            setRenaming(false);
          }}
          onCancel={() => setRenaming(false)}
        />
      ) : (
        <>
          <div
            style={{
              flexGrow: 1,
              flexShrink: 1,
              flexBasis: '0%',
              overflow: 'hidden',
            }}
          >
            <span
              style={{
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical' as any,
                WebkitLineClamp: 1,
                overflow: 'hidden',
                fontSize: 14,
                fontWeight: 500,
                color: '#1A1714',
                lineHeight: '18px',
                fontFamily: APP.fontFamily,
              }}
            >
              {folder.name}
            </span>
          </div>
          <span
            style={{
              flexShrink: 0,
              fontSize: 12,
              fontWeight: 400,
              color: '#8B7D6B',
              lineHeight: '16px',
              fontFamily: APP.fontFamily,
              whiteSpace: 'nowrap',
            }}
          >
            {countLabel}
          </span>
          <ChevronRight />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setRenaming(true);
            }}
            style={{
              flexShrink: 0,
              borderRadius: 6,
              paddingBlock: 5,
              paddingInline: 8,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontFamily: APP.fontFamily,
              fontSize: 12,
              fontWeight: 400,
              color: '#8B7D6B',
              lineHeight: '16px',
            }}
          >
            Rename
          </button>
          {onDownload && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDownload();
              }}
              style={{
                flexShrink: 0,
                borderRadius: 6,
                paddingBlock: 5,
                paddingInline: 8,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontFamily: APP.fontFamily,
                fontSize: 12,
                fontWeight: 400,
                color: '#8B7D6B',
                lineHeight: '16px',
              }}
            >
              Download
            </button>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              borderRadius: 6,
              paddingBlock: 5,
              paddingInline: 5,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            <TrashIcon />
          </button>
        </>
      )}
    </div>
  );
}

// ─── Main Panel ─────────────────────────────────

export default function FoldersPanel({
  folders,
  onCreateFolder,
  onCreateProjectFolder,
  recentProjectSources = [],
  onRenameFolder,
  onDeleteFolder,
  onDownloadFolder,
  onOpenFolder,
  onBack,
}: FoldersPanelProps) {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const rootFolders = folders.filter((folder) => !folder.parentId);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flexGrow: 1,
        height: '100%',
        fontFamily: APP.fontFamily,
        overflow: 'auto',
        paddingBlock: 40,
        paddingInline: 24,
      }}
    >
      <div style={{ width: '100%', maxWidth: 560, display: 'flex', flexDirection: 'column' }}>
        {/* Header with back button */}
        <div style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                aria-label="Go back"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: 'none',
                  border: `1px solid ${APP.border}`,
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M10 3L5 8l5 5" stroke="#8B7D6B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
            <div
              style={{
                fontSize: 24,
                fontWeight: 600,
                color: '#1A1714',
                lineHeight: '30px',
                letterSpacing: '-0.02em',
              }}
            >
              Projects
            </div>
          </div>
          <div
            style={{
              marginTop: 6,
              fontSize: 13,
              fontWeight: 400,
              color: '#8B7D6B',
              lineHeight: '16px',
            }}
          >
            Group documents, chats, and collaborators into projects. Click a project to open it.
          </div>
        </div>

        {/* New project button */}
          <div style={{ marginBottom: 16 }}>
            <NewFolderRow onCreate={() => setCreateModalOpen(true)} />
          </div>

        {/* Project list */}
        {rootFolders.length > 0 && (
          <div
            style={{
              borderRadius: 12,
              overflow: 'hidden',
              backgroundColor: '#FEFCF9',
              border: '1px solid #E0DCD4',
              boxShadow: '#1A17140A 0px 1px 2px',
            }}
          >
            {rootFolders.map((folder) => (
              <FolderRow
                key={folder.id}
                folder={folder}
                onOpen={() => onOpenFolder(folder.id)}
                onRename={(name) => onRenameFolder(folder.id, name)}
                onDelete={() => onDeleteFolder(folder.id)}
                onDownload={onDownloadFolder ? () => onDownloadFolder(folder.id) : undefined}
              />
            ))}
          </div>
        )}

        {rootFolders.length === 0 && (
          <div
            style={{
              textAlign: 'center',
              paddingBlock: 40,
              fontSize: 13,
              color: '#B8A898',
              lineHeight: '16px',
            }}
          >
            No projects yet. Create one above to get started.
          </div>
        )}
      </div>
      {createModalOpen && (
        <ProjectFolderCreateModal
          recentSources={recentProjectSources}
          onClose={() => setCreateModalOpen(false)}
          onCreate={async (input) => {
            if (onCreateProjectFolder) {
              await onCreateProjectFolder(input);
            } else {
              onCreateFolder(input.name);
            }
            setCreateModalOpen(false);
          }}
        />
      )}
    </div>
  );
}
